/**
 * 내부 십진 문자열 연산 — Number 미경유(대형 정수·소수 자릿수 무손실).
 * 파싱 규칙은 기존 `ixd-editor/value-validation.ts`의 strict 계약을 복제
 * (P1-4: 기존 파일 무접촉 — 중복은 P2-3에서 기존 쪽 제거로 해소).
 */

/** 부호·정수부·소수부로 분해한 십진 값. int/frac은 숫자만 담는다. */
export interface DecimalParts {
  negative: boolean;
  int: string;
  frac: string;
}

export const HYPHEN_ONLY_PATTERN = /^[-–—]$/;

const STRICT_DECIMAL_PATTERN =
  /^[+-]?(?:(?:\d{1,3}(?:,\d{3})+)|\d+)(?:\.\d+)?$/;

// 숫자 뒤에 단위 표기가 붙은 입력("1,200천원", "1,200주"). '%'는 단위가 아니라
// 비율 표기이므로 제외 — percent 전용 사유코드(bad-percent)가 따로 있다.
export const UNIT_SUFFIXED_NUMBER_PATTERN =
  /^\(?\s*[+-]?\d[\d,.\s]*\)?\s*[^\d\s,.()%+-]+$/;

/**
 * 값 앞뒤의 안 보이는 문자를 턴다. String.trim()은 Zs·개행류만 지우고 ZWSP(200B),
 * LRM/RLM(200E/200F), word joiner(2060), soft hyphen(00AD), 제어문자는 남긴다 —
 * 웹·PDF·HWP에서 복사한 값에 섞여 들어와 숫자 파싱을 통째로 실패시킨다.
 * 값 "안쪽"은 건드리지 않는다 ("1 234"를 1234로 삼키면 오입력이 조용히 통과한다).
 */
export const stripInvisibleEdges = (value: string): string =>
  value.replace(/^[\s\p{Cc}\p{Cf}]+|[\s\p{Cc}\p{Cf}]+$/gu, "");

/**
 * strict 십진 파싱 — 콤마 3자리 그룹 검증, 괄호 음수, 하이픈 단독(–— 포함)은 0.
 * 괄호 안 이중 부호·내부 공백·단위 접미사는 실패(null).
 */
export function parseStrictDecimal(value: string): DecimalParts | null {
  const trimmed = value.trim();

  if (trimmed === "") return null;
  if (HYPHEN_ONLY_PATTERN.test(trimmed)) {
    return { negative: false, int: "0", frac: "" };
  }

  const isParenNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const portion = isParenNegative ? trimmed.slice(1, -1).trim() : trimmed;

  if (portion === "") return null;
  if (isParenNegative && /[+-]/.test(portion)) return null;
  if (!STRICT_DECIMAL_PATTERN.test(portion)) return null;

  let body = portion.replace(/,/g, "");
  let negative = isParenNegative;
  if (body.startsWith("+")) {
    body = body.slice(1);
  } else if (body.startsWith("-")) {
    negative = true;
    body = body.slice(1);
  }

  const dotIdx = body.indexOf(".");
  return dotIdx >= 0
    ? { negative, int: body.slice(0, dotIdx), frac: body.slice(dotIdx + 1) }
    : { negative, int: body, frac: "" };
}

/**
 * decimalDigits "미지정" 판정의 유일한 집 — null/undefined/음수는 미지정(null).
 * 저장 절삭(truncateFraction)·표시 반올림(stored-to-view)·편집 초기값(어댑터)이
 * 같은 기준을 봐야 한 곳만 바뀌어 어긋나는 일이 없다.
 */
export const resolveDecimalDigits = (
  digits: number | null | undefined,
): number | null =>
  digits !== null && digits !== undefined && digits >= 0 ? digits : null;

/**
 * 소수점 아래 자릿수 절삭(truncate, 0 방향). digits 미지정이면 원값.
 * 표시 경로(stored-to-view)의 half-up 반올림과 의도적으로 다르다 — 입력은 보수적 버림.
 */
export const truncateFraction = (
  value: DecimalParts,
  digits: number | null | undefined,
): DecimalParts => {
  const resolved = resolveDecimalDigits(digits);
  return resolved === null
    ? value
    : { ...value, frac: value.frac.slice(0, resolved) };
};

/**
 * ×10^k — 소수점 우측 이동 (k=0은 정상 no-op).
 * 비정상 scale은 조용히 무시하면 저장값 자릿수가 틀어진 채 통과하므로 fail-loud
 * (convention-runtime-stability: 계약 위반은 throw).
 */
export function shiftPointRight(value: DecimalParts, k: number): DecimalParts {
  if (!Number.isInteger(k) || k < 0) {
    throw new Error(`scale은 0 이상의 정수여야 한다: ${k}`);
  }
  if (k === 0) return value;
  const frac = value.frac.padEnd(k, "0");
  return {
    negative: value.negative,
    int: value.int + frac.slice(0, k),
    frac: frac.slice(k),
  };
}

/** ÷10^k — 소수점 좌측 이동 (k≤0이면 원값) */
export function shiftPointLeft(value: DecimalParts, k: number): DecimalParts {
  if (!Number.isInteger(k) || k <= 0) return value;
  const int = value.int.padStart(k + 1, "0");
  return {
    negative: value.negative,
    int: int.slice(0, int.length - k),
    frac: int.slice(int.length - k) + value.frac,
  };
}

export const isDecimalZero = (value: DecimalParts): boolean =>
  /^0*$/.test(value.int) && /^0*$/.test(value.frac);

/** canonical 문자열 — 선행·트레일링 제로 제거, -0은 "0" */
export function toCanonicalString(value: DecimalParts): string {
  const int = value.int.replace(/^0+(?=\d)/, "") || "0";
  const frac = value.frac.replace(/0+$/, "");
  const isZero = int === "0" && frac === "";
  const sign = value.negative && !isZero ? "-" : "";
  return `${sign}${int}${frac ? `.${frac}` : ""}`;
}

/** 정수부 천 단위 콤마 (숫자만으로 이뤄진 문자열 전제) */
export const groupThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** fracPart를 digits 자리로 half-up 반올림 (부족하면 0 패딩). 캐리는 intPart로 전파. */
export function roundHalfUp(
  intPart: string,
  fracPart: string,
  digits: number,
): [string, string] {
  if (fracPart.length <= digits) {
    return [intPart, fracPart.padEnd(digits, "0")];
  }
  const kept = fracPart.slice(0, digits);
  const roundDigit = fracPart.charCodeAt(digits) - 48;
  if (roundDigit < 5) {
    return [intPart, kept];
  }
  const arr = (intPart + kept).split("");
  let carry = 1;
  for (let i = arr.length - 1; i >= 0 && carry; i--) {
    const v = arr[i].charCodeAt(0) - 48 + carry;
    arr[i] = String(v % 10);
    carry = v >= 10 ? 1 : 0;
  }
  if (carry) arr.unshift("1");
  const combined = arr.join("");
  if (digits === 0) return [combined, ""];
  const intLength = combined.length - digits;
  return [combined.slice(0, intLength) || "0", combined.slice(intLength)];
}
